import api from '@/api/axios';
import type { FileInfo, RowStatus, LibraryItem, LibraryApiResponse } from './libraryService';

export interface TrainingTypeInfo {
  id: number;
  nombre: string;
  descripcion: string;
  rowStatus: RowStatus;
}

export interface TrainingGroupInfo {
  id: number;
  grupo?: {
    id: number;
    nombre: string;
    authUuid?: string;
    rowStatus: RowStatus;
  };
  rowStatus: RowStatus;
}

export interface TrainingUserInfo {
  id: number;
  authUuid?: string;
  fechaAsignacion: string;
  rowStatus: RowStatus;
}

export interface ElementTypeOption {
  id: number;
  nombre: string;
  descripcion: string;
  rowStatus: RowStatus;
}

export interface TrainingElementInfo {
  id: number;
  nombre: string;
  orden: number;
  tipoElementoCapacitacion: ElementTypeOption;
  bibliotecaMaterial?: LibraryItem;
  cuestionario?: unknown;
  rowStatus: RowStatus;
}

export interface TrainingItem {
  id: number;
  nombre: string;
  descripcion: string;
  archivoImagen: FileInfo | null;
  fechaInicio: string;
  fechaLimite: string;
  esCompetencia: boolean;
  duracion: string;
  calificacion: number;
  archivoInsignia: FileInfo | null;
  nombreInstructor: string;
  rowStatus: RowStatus;
  tipoCapacitacion: TrainingTypeInfo;
  grupos: TrainingGroupInfo[];
  usuarios: TrainingUserInfo[];
  elementos: TrainingElementInfo[];
}

export interface TrainingDataResponse {
  content: TrainingItem[];
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  empty: boolean;
}

export interface TrainingApiResponse {
  code: string;
  message: string;
  data: TrainingDataResponse;
}

export interface TrainingTypesApiResponse {
  code: string;
  message: string;
  data: TrainingTypeInfo[];
}

export interface ElementTypesApiResponse {
  code: string;
  message: string;
  data: ElementTypeOption[];
}

class TrainingService {
  private readonly basePath = '/appy/capacitacion/v1';
  private readonly typesPath = '/appy/tipo/capacitacion/v1';
  private readonly elementTypesPath = '/appy/tipo/elemento/capacitacion/v1';
  private readonly libraryPath = '/appy/biblioteca/v1';
  private readonly questionnairePath = '/appy/cuestionario/v1';

  async getItems(page: number = 1, size: number = 15, nombre?: string): Promise<TrainingApiResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('size', String(size));
    if (nombre && nombre.trim()) {
      params.append('nombre', nombre.trim());
    }
    const response = await api.get<TrainingApiResponse>(`${this.basePath}?${params.toString()}`);
    return response.data;
  }

  async getItemById(id: number): Promise<{ code: string; message: string; data: TrainingItem }> {
    const response = await api.get(`${this.basePath}/${id}`);
    return response.data;
  }

  async getTypes(): Promise<TrainingTypesApiResponse> {
    const response = await api.get<TrainingTypesApiResponse>(this.typesPath);
    return response.data;
  }

  async getElementTypes(): Promise<ElementTypesApiResponse> {
    const response = await api.get<ElementTypesApiResponse>(this.elementTypesPath);
    return response.data;
  }

  async searchLibraryItems(search?: string): Promise<LibraryApiResponse> {
    const params = new URLSearchParams();
    params.append('page', '1');
    params.append('size', '50');
    if (search?.trim()) params.append('titulo', search.trim());
    const response = await api.get<LibraryApiResponse>(`${this.libraryPath}?${params.toString()}`);
    return response.data;
  }

  async searchQuestionnaires(search?: string): Promise<{ data: { content: { id: number; nombre: string }[] } }> {
    const params = new URLSearchParams();
    params.append('page', '1');
    params.append('size', '50');
    if (search?.trim()) params.append('nombre', search.trim());
    const response = await api.get(`${this.questionnairePath}?${params.toString()}`);
    return response.data;
  }

  async createItem(data: FormData): Promise<TrainingApiResponse> {
    const response = await api.post<TrainingApiResponse>(this.basePath, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async updateItem(data: FormData): Promise<TrainingApiResponse> {
    const response = await api.put<TrainingApiResponse>(`${this.basePath}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async deleteItem(id: number): Promise<void> {
    await api.delete(`${this.basePath}/${id}`);
  }
}

export const trainingService = new TrainingService();
export default trainingService;